import { isEmpty } from "lodash";
import { ActionContext } from "vuex";

import AdvertisersApi from "@/api/AdvertisersApi";
import Api from "@/api/Api";
import { ALL } from "@/constants";
import { PROJECTS_CATALOGS_KEY, RAW_PROJECTS_CATALOGS_KEY, getCached, setCached } from "@/helpers/catalogsHelper";
import Lang from "@/lang";
import { API_ROUTES, requestTokens } from "@/settings/apiRoutes";
import store, { RootState } from "@/store";

export type Catalogs = {
  [key: string]: string[] | Record<string, string[]>;
};

export interface CatalogsState {
  catalogs: Catalogs | null;
  isLoading: boolean;
  rawCatalogs?: Catalogs | null;
  advertisers: Record<string, string>[];
}

interface CatalogsDictionary {
  [key: string]: string;
}

export const projectsCatalogsDictionary: CatalogsDictionary = {
  agencies: "agency",
  advertisers: "advertiser",
  business_goal_property_values: "businessGoalPropertyValue",
  is_approved: "isApproved",
  projects: "project",
  business_goal_object_names: "businessGoalObjectName",
  plans: "plan",
  scenarios: "scenario",
  project_owned_by: "projectOwnedBy",
  business_goal_owned_by: "businessGoalOwnedBy",
  plan_owned_by: "planOwnedBy",
  scenario_owned_by: "scenarioOwnedBy",
  advertiser_profile: "advertiserProfile",
};

const projectsCatalogsDefault: Catalogs = {
  agency: [],
  advertiser: [],
  businessGoalPropertyValue: [],
  isActive: [],
  project: [],
  businessGoalObjectName: [],
  plan: [],
  scenario: [],
  projectOwnedBy: [],
  businessGoalOwnedBy: [],
  planOwnedBy: [],
  scenarioOwnedBy: [],
  advertiserProfile: {},
};

const catalogState: CatalogsState = {
  catalogs: null,
  rawCatalogs: null,
  isLoading: false,
  advertisers: [],
};

export default {
  namespaced: true,

  state: catalogState,

  mutations: {
    updateProjectsCatalogs(state: CatalogsState, catalogs: Catalogs): void {
      state.catalogs = catalogs;
    },

    updateAdvertisersList(state: CatalogsState, advertisers: Record<string, string>[]): void {
      state.advertisers = advertisers;
    },

    updateRawProjectsCatalogs(state: CatalogsState, catalogs: Catalogs): void {
      state.rawCatalogs = catalogs;
    },

    updateLoading(state: CatalogsState, val: boolean): void {
      state.isLoading = val;
    },

    resetCatalog(state: CatalogsState) {
      state.catalogs = null;
    },
  },

  actions: {
    async fetchProjectsCatalogs(ctx: ActionContext<CatalogsState, RootState>): Promise<void> {
      const cached = getCached(PROJECTS_CATALOGS_KEY);
      if (cached) {
        ctx.commit("updateProjectsCatalogs", cached);
        return;
      }

      ctx.commit("updateLoading", true);
      type CatalogsData = Record<string, string[] | Record<string, string>[]>;
      const filterData = {} as CatalogsData;
      const res = await Api.requestWithRetry<CatalogsData>({
        path: API_ROUTES.PROJECT_CATALOG.PATH,
        defaultResult: {},
        errorCallback: async () => {
          await ctx.commit("updateProjectsCatalogs", projectsCatalogsDefault);
        },
        token: requestTokens.apiProjectsCatalogs,
      });

      if (res.campaign_ids) {
        res.campaign_ids = Array.from(
          new Set((res.campaign_ids as Record<string, string>[]).map((value: Record<string, string>) => value.id))
        );
      }

      if (isEmpty(res)) {
        store.commit("app/showAlert", { message: Lang.errorGetCatalogs, type: "error" });
      } else {
        const skipSortingAndAll = ["advertiser_profile", "subindustries"];

        for (const key in res) {
          const dictionaryKey = projectsCatalogsDictionary[key as string];
          if (!dictionaryKey) {
            continue;
          }

          if (skipSortingAndAll.includes(key)) {
            filterData[dictionaryKey] = res[key];
            continue;
          }

          filterData[dictionaryKey] = res[key]?.sort();

          if (Array.isArray(filterData[dictionaryKey])) {
            (filterData[dictionaryKey] as string[]).unshift(ALL);
            filterData[dictionaryKey] = [...new Set(filterData[dictionaryKey] as string[])];
          }
        }

        ctx.commit("updateProjectsCatalogs", filterData);
        setCached(PROJECTS_CATALOGS_KEY, filterData);
      }

      ctx.commit("updateLoading", false);
    },

    async fetchRawProjectsCatalogs(ctx: ActionContext<CatalogsState, RootState>, skipCache = false): Promise<void> {
      if (!skipCache) {
        const cached = getCached(RAW_PROJECTS_CATALOGS_KEY);
        if (cached) {
          ctx.commit("updateRawProjectsCatalogs", cached);
          return;
        }
      }

      ctx.commit("updateLoading", true);

      const res = await Api.requestWithRetry<Catalogs>({
        path: API_ROUTES.PROJECT_CATALOG.PATH,
        defaultResult: {},
        errorCallback: async () => {
          ctx.commit("updateProjectsCatalogs", projectsCatalogsDefault);
        },
        token: requestTokens.apiProjectsCatalogs,
      });

      if (!isEmpty(res)) {
        ctx.commit("updateRawProjectsCatalogs", res);
        setCached(RAW_PROJECTS_CATALOGS_KEY, res as unknown as Record<string, string[] | Record<string, string>[]>);
      } else {
        store.commit("app/showAlert", { message: Lang.errorGetCatalogs, type: "error" });
      }

      ctx.commit("updateLoading", false);
    },

    async fetchAdvertisers(ctx: ActionContext<CatalogsState, RootState>) {
      try {
        ctx.commit("updateLoading", true);
        const api = new AdvertisersApi();
        const data = await api.getAllAdvertisers();
        ctx.commit("updateAdvertisersList", data);
      } catch (e) {
        store.commit("app/showAlert", { message: Lang.errorGetAdvertisers, type: "error" });
      } finally {
        ctx.commit("updateLoading", false);
      }
    },
  },

  getters: {
    catalogs(state: CatalogsState): Catalogs | null {
      return state.catalogs;
    },

    advertisers(state: CatalogsState): Record<string, string>[] | [] {
      return state.advertisers;
    },

    rawCatalogs(state: CatalogsState): Catalogs | null {
      return state.rawCatalogs || null;
    },

    isLoading(state: CatalogsState): boolean {
      return state.isLoading;
    },
  },
};
